// src/modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CancelNfceModal/index.jsx
// *******************************************************************************
// CANCEL NFCe MODAL - Cancelamento de NFC-e autorizada
// *******************************************************************************
import { useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { createRoot } from 'react-dom/client';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import cancelNfceModalStep from './Step1';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CancelNfceModal = ({
  show,
  setShow,
  afterClose,
  company,
  invoiceId,
  invoiceNumber,
  isNfce,
  onSuccess,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [resultData, setResultData] = useState(null);
  const [showResult, setShowResult] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // MEMOS
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [cancelNfceModalStep({ company, invoiceId, invoiceNumber, resultData, showResult, isNfce })],
    [company, invoiceId, invoiceNumber, resultData, showResult, isNfce]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form, setLoading }) => {
    if (showResult) {
      return true;
    }

    const values = form?.getFieldsValue(true) || {};
    const companyId = company?._id || company?.entityId;
    const finalInvoiceId = invoiceId || values.invoiceId;

    const payload = {
      companyId,
      invoiceId: finalInvoiceId,
      justification: values.justificativa,
    };

    // Backend /nfce/cancel handles both NF-e and NFC-e (via invoice.documentType)
    const cancelUrl = API.FISCAL.NFCE_CANCEL;

    setLoading(true);

    try {
      const response = await axiosService.post({
        url: cancelUrl,
        data: payload,
      });

      console.log('[CancelNfceModal] Full response:', response);

      // Handle axiosService response format (can be array or object)
      let responseData;
      if (Array.isArray(response)) {
        // axiosService returns [data, summary] when returnAsObject is used
        responseData = response[0];
      } else {
        responseData = response;
      }

      console.log('[CancelNfceModal] Response data:', JSON.stringify(responseData, null, 2));

      // Check success at root level (routerService wraps response)
      const isSuccess = responseData?.success === true;
      const hasData = responseData?.data?.success === true;

      console.log('[CancelNfceModal] Success check:', { isSuccess, hasData });

      if (isSuccess || hasData) {
        // Get data from either root or data property
        const successData = hasData ? responseData.data : responseData;

        // Normalize xml field - backend may return xml, xmlContent, xmlCancelamento, etc.
        const xmlContent =
          successData.xml ||
          successData.xmlContent ||
          successData.xmlCancelamento ||
          successData.cancelXml ||
          null;

        // Normalize xmlStorageUrl - backend may return different field names
        const xmlUrl =
          successData.xmlStorageUrl ||
          successData.xmlUrl ||
          successData.cancelXmlUrl ||
          successData.xmlCancelamentoUrl ||
          null;

        const result = {
          success: true,
          protocolNumber: successData.protocolNumber || successData.protocol,
          cancelDate: successData.cancelDate,
          invoiceId: finalInvoiceId,
          xml: xmlContent,
          xmlStorageUrl: xmlUrl,
        };

        setResultData(result);
        setShowResult(true);
        onSuccess?.(result);

        return false;
      } else {
        // Get error details from response
        const errorDetails = responseData?.raw || responseData;

        // Build meaningful error message
        let errorMsg = translateX('cancel_nfce_error');
        let errorCode = null;

        // Check for specific SEFAZ errors (following VoidNfceModal pattern)
        if (responseData?.errors?.[0]?.error) {
          errorMsg = responseData.errors[0].error;
          errorCode = responseData.errors[0].code;
        } else if (errorDetails?.motivo_status) {
          // NuvemFiscal rejection
          errorMsg = errorDetails.motivo_status;
          errorCode = errorDetails.codigo_status;
        } else if (errorDetails?.errors?.[0]?.message) {
          errorMsg = errorDetails.errors[0].message;
        } else if (errorDetails?.error?.message) {
          errorMsg = errorDetails.error.message;
        } else if (responseData?.message) {
          errorMsg = responseData.message;
        } else if (responseData?.error) {
          errorMsg = responseData.error;
        }

        console.log('[CancelNfceModal] Error details:', {
          responseData,
          errorDetails,
          errorMsg,
          errorCode,
        });

        // Error xml may be at root or inside data
        const errorData = responseData?.data || responseData;
        const errorXmlContent =
          errorData?.xml ||
          errorData?.xmlContent ||
          errorData?.xmlCancelamento ||
          errorData?.cancelXml ||
          null;
        const errorXmlUrl =
          errorData?.xmlStorageUrl ||
          errorData?.xmlUrl ||
          errorData?.cancelXmlUrl ||
          errorData?.xmlCancelamentoUrl ||
          null;

        setResultData({
          success: false,
          error: errorMsg,
          code: errorCode,
          xml: errorXmlContent,
          xmlStorageUrl: errorXmlUrl,
        });
        setShowResult(true);

        return false;
      }
    } catch (error) {
      console.error('[CancelNfceModal] Error:', error);

      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        translateX('cancel_nfce_error');

      setResultData({
        success: false,
        error: errorMsg,
      });
      setShowResult(true);

      return false;
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title={showResult ? (isNfce ? 'cancel_nfce_result' : 'Resultado do Cancelamento') : (isNfce ? 'cancel_nfce_title' : 'Cancelamento de NF-e')}
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={520}
      maskClosable={false}
      nextButtonText={showResult ? 'close' : 'cancel_invoice'}
      nextButtonProps={showResult ? {} : { danger: true }}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION
// *******************************************************************************
const showCancelNfceModal = ({ company, invoiceId, invoiceNumber, isNfce, onSuccess } = {}) => {
  const container = document.createElement('div');
  container.id = 'cancel-nfce-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);
  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <CancelNfceModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          company={company}
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          isNfce={isNfce}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default showCancelNfceModal;
export { CancelNfceModal };
